import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { 
  Mail, Send, Copy, CheckCircle, DollarSign, MapPin, 
  Truck, AlertCircle, Sparkles, Calculator
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailAssistant = () => {
  const [emailContent, setEmailContent] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [suggestedResponse, setSuggestedResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Rate calculator state
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [rateResult, setRateResult] = useState(null);

  const analyzeEmail = async () => {
    if (!emailContent.trim()) {
      toast.error('Please paste an email to analyze');
      return;
    }
    
    setLoading(true);
    try {
      // Analyze email
      const analysisRes = await axios.post(`${API}/ai/analyze-email`, {
        email_content: emailContent
      });
      setAnalysis(analysisRes.data);
      
      // Generate response
      const responseRes = await axios.post(`${API}/ai/generate-response`, {
        email_content: emailContent
      });
      setSuggestedResponse(responseRes.data.suggested_response);
      
      toast.success('Email analyzed successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error analyzing email');
    } finally {
      setLoading(false);
    }
  };

  const calculateRate = async () => {
    if (!origin || !destination) {
      toast.error('Please enter origin and destination');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/ai/calculate-rate`, {
        origin,
        destination,
        equipment_type: 'flatbed'
      });
      setRateResult(res.data);
    } catch (error) {
      toast.error('Error calculating rate');
    }
  };

  const copyResponse = () => {
    navigator.clipboard.writeText(suggestedResponse);
    setCopied(true);
    toast.success('Response copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      <Toaster position="top-center" richColors />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-yellow-400" />
              AI Email Assistant
            </h1>
            <p className="text-slate-400">Paste broker emails to get instant rate quotes and responses</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Email Input */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                Incoming Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Paste the broker/shipper email here..."
                className="min-h-[300px] bg-slate-900 border-slate-600 text-white"
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
              />
              <Button 
                onClick={analyzeEmail} 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Analyzing...' : 'Analyze Email & Generate Response'}
              </Button>
            </CardContent>
          </Card>

          {/* Analysis Results */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Analysis Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analysis ? (
                <div className="space-y-4">
                  {/* Load Request Badge */}
                  <div className="flex items-center gap-2">
                    {analysis.is_load_request ? (
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                        ✅ Load Request Detected
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium">
                        ⚠️ General Inquiry
                      </span>
                    )}
                    {analysis.is_dedicated && (
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
                        🔄 Dedicated/Weekly
                      </span>
                    )}
                  </div>

                  {/* Equipment & Urgency */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-900 rounded-lg">
                      <p className="text-slate-400 text-xs mb-1">Equipment</p>
                      <p className="text-white font-medium flex items-center gap-2">
                        <Truck className="w-4 h-4 text-blue-400" />
                        {analysis.equipment_type?.toUpperCase()}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-900 rounded-lg">
                      <p className="text-slate-400 text-xs mb-1">Urgency</p>
                      <p className={`font-medium ${analysis.urgency === 'high' ? 'text-red-400' : 'text-green-400'}`}>
                        {analysis.urgency === 'high' ? '🔥 HIGH' : '✅ Normal'}
                      </p>
                    </div>
                  </div>

                  {/* Locations */}
                  {analysis.locations_found?.length > 0 && (
                    <div className="p-3 bg-slate-900 rounded-lg">
                      <p className="text-slate-400 text-xs mb-2">Locations Detected</p>
                      <div className="flex flex-wrap gap-2">
                        {analysis.locations_found.map((state, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {state}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rate Quotes */}
                  {analysis.rate_quotes?.length > 0 && (
                    <div className="p-3 bg-slate-900 rounded-lg">
                      <p className="text-slate-400 text-xs mb-2">💰 Suggested Rates (Competitive)</p>
                      <div className="space-y-2">
                        {analysis.rate_quotes.map((quote, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-slate-800 rounded">
                            <span className="text-white text-sm">
                              {quote.origin} → {quote.destination}
                            </span>
                            <div className="text-right">
                              <span className="text-green-400 font-bold">
                                ${quote.competitive_rate?.toLocaleString()}
                              </span>
                              <span className="text-slate-500 text-xs ml-2">
                                ({quote.distance_miles} mi)
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Paste an email and click analyze to see results</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Suggested Response */}
        {suggestedResponse && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-green-400" />
                Suggested Response
              </CardTitle>
              <Button 
                onClick={copyResponse}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Response'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-900 p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-white text-sm font-sans">
                  {suggestedResponse}
                </pre>
              </div>
              <p className="text-slate-400 text-xs mt-3">
                💡 Tip: Review and customize the response before sending. Respond within 30 minutes for best results!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Rate Calculator */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-400" />
              Quick Rate Calculator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Origin (City, State)</label>
                <Input 
                  placeholder="e.g., Vernon, CA"
                  className="bg-slate-900 border-slate-600 text-white"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Destination (City, State)</label>
                <Input 
                  placeholder="e.g., Hendersonville, NC"
                  className="bg-slate-900 border-slate-600 text-white"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                />
              </div>
              <Button onClick={calculateRate} className="bg-orange-500 hover:bg-orange-600">
                Calculate Rate
              </Button>
              {rateResult && (
                <div className="p-3 bg-green-500/20 rounded-lg text-center">
                  <p className="text-green-400 text-2xl font-bold">
                    ${rateResult.competitive_rate?.toLocaleString()}
                  </p>
                  <p className="text-slate-400 text-xs">
                    {rateResult.distance_miles} mi @ ${rateResult.rate_per_mile}/mi
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmailAssistant;
